import { type ComponentPropsWithoutRef, type ReactNode, useEffect, useLayoutEffect, useReducer, useRef, useState } from 'react';
import { Button, type ButtonProps } from '@zep-us/zus/button';
import { breakpoints, ICON_COLOR } from '@zep-us/zus/tokens';
import { cn } from '@zep-us/zus/utils';
import { type IconSize, XIcon } from '@zep-us/zus-icons';
import type { PopupActions, ResolvedCampaign } from '../types';
import { appendTrackingId, toSafeOpenUrl } from './PopupCard/utils';

// 삭제된 quiz-app `module/Notice` 의 PopupNotice UI 를 @zep/crm 로 이관한 공지 스타일
// 캠페인 카드. 우하단(BOTTOM_RIGHT)·모바일 하단 노출용이며 mobile/withImage/withoutImage
// 3종 변형은 원본 공지 팝업과 동일하다.
//
// PopupCard(inline-style, 프레임워크 무관)와 달리 이 카드는 ZEP 디자인시스템
// `@zep-us/zus`(Button·XIcon·토큰·cn·shadow 프리셋) 만으로 구성한다 — 소비 앱이 전부
// ZEP 앱(모두 ZUS + Tailwind 사용)이라는 전제로 포터블 원칙을 개정한 결과. 소비 앱은
// tailwind content 에 `packages/crm/src` 를 포함하고 ZUS preset 을 써야 클래스가 컴파일된다.
// CENTER_MODAL 은 renderNoticePopup 이 기존 PopupCard(CenterModalCard)로 위임하므로
// 여기서는 다루지 않는다(BOTTOM_RIGHT 전용).

// 공지 팝업은 채널톡 런처 등 떠 있는 위젯 위에 보여야 하므로 충분히 높은 z-index 를 준다.
// (quiz 의 z-notice=9999998 을 하드코딩하지 않고 그보다 높은 공통 상수로 둔다.)
const NOTICE_POPUP_Z_INDEX = 10_000_000;

/** 공지 원본과 동일 기준으로 tabletLg 이하를 모바일로 본다. matchMedia 직접 사용(신규 dep 없음). */
function useIsMobile(): boolean {
  // 초기값을 effect 가 아니라 렌더 시점에 동기 판정한다. false 로 시작하면 캐러셀
  // 재마운트 첫 프레임이 데스크톱 분기(우하단 배치)로 그려지는데, 등장 delay 를
  // 제거한 뒤로는 그 프레임이 가려지지 않아 모바일에서 회전마다 깜빡인다(codex P2).
  // SSR 에서는 window 가 없으므로 false 폴백(소비 앱은 client-only 마운트).
  const [isMobile, setIsMobile] = useState(() => typeof window !== 'undefined' && window.matchMedia(`(max-width: ${breakpoints.tabletLg}px)`).matches);
  useEffect(() => {
    const mql = window.matchMedia(`(max-width: ${breakpoints.tabletLg}px)`);
    const update = () => setIsMobile(mql.matches);
    update();
    mql.addEventListener('change', update);
    return () => mql.removeEventListener('change', update);
  }, []);
  return isMobile;
}

/** 데스크톱 카드의 화면 가장자리 여백(px). 호스트 앱의 플로팅 버튼을 피할 때 쓴다. */
export interface NoticeCardOffset {
  bottom?: number;
  right?: number;
}
interface NoticeCardProps {
  campaign: ResolvedCampaign;
  actions: PopupActions;
  desktopOffset?: NoticeCardOffset;
}

// ── 캐러셀 전환: 프레임 유지, 내용만 좌우 롤링 ────────────────────
// 카드 틀(Container)은 마운트·크기를 유지하고, 회전 시 안의 내용(이미지/텍스트/버튼)만
// 트랙처럼 좌우로 굴러 교체된다: 이전 내용은 왼쪽으로 밀려나가고 새 내용이 오른쪽에서
// 같은 속도로 따라 들어온다. 카드 전체 재마운트(구 방식)는 빈 화면·야바위 인상을 줬고,
// 카드가 바닥 고정(bottom anchor)이라 높이 변화가 "위로 자라는" 움직임으로 보이므로,
// 전환 동안 프레임 높이를 이전 값으로 고정하고 전환이 끝난 뒤(이미지 로드까지 확인 후)
// 높이가 실제로 다를 때만 조용히 맞춘다.
const ROLL_MS = 350;
// 정확히 ±100% 여야 한다: 그보다 크면 나가는 내용과 들어오는 내용 사이에 투명한 틈이
// 생겨 전환 내내 카드 배경이 비어 보이는 seam(잔상감)이 함께 굴러간다. 100% 면 두 내용의
// 가장자리가 맞붙어 필름스트립처럼 끊김 없이 이어진다.
const SWAP_KEYFRAMES = `
@keyframes crm-notice-roll-out { from { transform: translateX(0); } to { transform: translateX(-100%); } }
@keyframes crm-notice-roll-in { from { transform: translateX(100%); } to { transform: translateX(0); } }
@media (prefers-reduced-motion: reduce) {
  [data-crm-swap] { animation-duration: 1ms !important; transition-duration: 1ms !important; }
}
`;

// keyframes 는 모듈당 1회만 <head> 에 주입한다. 인스턴스별 <style> 렌더는 NoticeCard 를
// 발행본으로 소비하는 admin-web 미리보기(다중 인스턴스)에서 중복 주입되기 때문.
let isSwapKeyframesInjected = false;
function ensureSwapKeyframes() {
  if (isSwapKeyframesInjected || typeof document === 'undefined') return;
  isSwapKeyframesInjected = true;
  const style = document.createElement('style');
  style.textContent = SWAP_KEYFRAMES;
  document.head.appendChild(style);
}
interface OutgoingContent {
  key: string;
  node: ReactNode;
  /** 퇴장 시작 시점에 고정할 프레임 높이(px). 직전 커밋에서 측정된 값. */
  height: number;
}

// ── ContentSwap 상태 모델 ─────────────────────────────────────────
// 전환은 상태 4개(현재 key·퇴장 내용·settle 트리거·프레임 높이)가 정해진 순서로만
// 움직여야 해서, 산발적 useState 대신 단일 reducer 로 전이를 한곳에 모은다(코드리뷰
// 요청). 타이머·DOM 측정 같은 부수효과는 여전히 effect 가 소유하고, reducer 는 순수
// 상태 전이만 담당한다.
export interface SwapState {
  /** 현재 트랙에 렌더 중인 캠페인 key */
  displayedKey: string;
  /** 퇴장 롤링 중인 직전 내용. null = 전환 중 아님 */
  outgoing: OutgoingContent | null;
  /**
   * 롤링 종료 후 높이 정착(settle) 트리거. 0 = 대기 없음. roll 과 settle 을 한
   * effect 에 두면 rollDone 이 자기 effect 의 cleanup 을 유발해 settle 타이머까지
   * 지워버리는 자기 취소 버그가 있었다(코드리뷰 P1) — seq 를 별도 필드로 분리해
   * settle 은 자기 effect 에서 처리한다.
   */
  settleSeq: number;
  /** 프레임 고정/트랜지션 높이. undefined = auto */
  height: string | undefined;
}
export type SwapAction = /** 새 캠페인 도착: 직전 커밋 스냅샷을 퇴장용으로 보관, 진행 중 settle 취소 */
{
  type: 'swap';
  key: string;
  snapshot: OutgoingContent;
}
/** 퇴장 시작: 프레임 높이를 직전 높이로 고정 */ | {
  type: 'lockHeight';
  heightPx: number;
}
/** 롤링 종료: 퇴장 내용 제거 + settle 예약 */ | {
  type: 'rollDone';
}
/** settle: 새 내용의 실제 높이로 트랜지션 */ | {
  type: 'settleTo';
  heightPx: number;
}
/** 높이 고정 해제(auto 복귀) */ | {
  type: 'releaseHeight';
};

// 테스트에서 jsdom(offsetHeight=0)이 못 태우는 높이 경로까지 순수 함수로 검증할 수
// 있도록 export 한다. 런타임 소비자는 ContentSwap 뿐.
export function swapReducer(state: SwapState, action: SwapAction): SwapState {
  switch (action.type) {
    case 'swap':
      return {
        ...state,
        displayedKey: action.key,
        outgoing: action.snapshot,
        settleSeq: 0
      };
    case 'lockHeight':
      return {
        ...state,
        height: `${action.heightPx}px`
      };
    case 'rollDone':
      // 퇴장 중이 아닐 때의 rollDone 은 불법 전이다. rollTimer 배선상 도달하지 않지만,
      // dispatch 지점이 늘어나도 settleSeq 이중 증가가 새지 않게 reducer 가 차단한다.
      if (!state.outgoing) return state;
      return {
        ...state,
        outgoing: null,
        settleSeq: state.settleSeq + 1
      };
    case 'settleTo':
      return {
        ...state,
        height: `${action.heightPx}px`
      };
    case 'releaseHeight':
      // 이미 auto 면 참조를 보존해 no-op 리렌더를 막는다(구 useState 의 Object.is
      // bail-out 과 동등).
      if (state.height === undefined) return state;
      return {
        ...state,
        height: undefined
      };
  }
}
function ContentSwap({
  swapKey,
  children
}: {
  swapKey: string;
  children: ReactNode;
}) {
  const [state, dispatch] = useReducer(swapReducer, swapKey, key => ({
    displayedKey: key,
    outgoing: null,
    settleSeq: 0,
    height: undefined
  }));
  const {
    displayedKey,
    outgoing,
    settleSeq,
    height
  } = state;
  const wrapRef = useRef<HTMLDivElement>(null);
  const currentRef = useRef<HTMLDivElement>(null);

  // 직전 커밋의 내용/높이 스냅샷. 렌더 중 DOM 을 읽지 않기 위해(React 규칙) 커밋 후
  // effect 에서만 갱신하고, 스왑 렌더에서는 이 스냅샷을 퇴장 콘텐츠로 승격한다.
  // 현재 내용은 스냅샷이 아니라 children 을 직접 렌더하므로 같은 캠페인의 내용 갱신
  // (폴링 updatedAt 변경)도 즉시 반영된다(코드리뷰 P2).
  const committedRef = useRef<{
    key: string;
    node: ReactNode;
    height: number;
  }>({
    key: swapKey,
    node: children,
    height: 0
  });
  useEffect(() => {
    committedRef.current = {
      key: swapKey,
      node: children,
      height: wrapRef.current?.offsetHeight ?? 0
    };
  });

  // 이미지 로드처럼 리렌더 없이 내용 높이가 바뀌면 위 스냅샷이 로드 전 높이로 남아,
  // 다음 전환의 lockHeight 가 퇴장 카드 하단을 잘라낸다. 크기 변화마다 높이만 갱신한다.
  useEffect(() => {
    const current = currentRef.current;
    if (!current || typeof ResizeObserver === 'undefined') return;
    const observer = new ResizeObserver(() => {
      if (committedRef.current.key !== displayedKey) return;
      committedRef.current.height = current.offsetHeight;
    });
    observer.observe(current);
    return () => observer.disconnect();
  }, [displayedKey]);

  // 렌더 중 상태 갱신(React 공식 adjust-state-on-prop-change 패턴): 새 캠페인이 오면
  // 직전 커밋 스냅샷을 퇴장용으로 보관하고, 진행 중이던 settle 은 취소한다.
  if (swapKey !== displayedKey) {
    dispatch({
      type: 'swap',
      key: swapKey,
      snapshot: {
        ...committedRef.current
      }
    });
  }

  // 퇴장 시작: 프레임 높이를 직전 높이로 고정하고, 롤링이 끝나면 퇴장 내용을 제거한 뒤
  // settle 을 예약한다. 페인트 전에 높이를 고정해야 하므로 layout effect 를 쓴다.
  useLayoutEffect(() => {
    if (!outgoing) return;
    if (outgoing.height > 0) {
      dispatch({
        type: 'lockHeight',
        heightPx: outgoing.height
      });
    }
    const rollTimer = window.setTimeout(() => {
      dispatch({
        type: 'rollDone'
      });
    }, ROLL_MS + 30);
    return () => window.clearTimeout(rollTimer);
    // key 변화에만 반응한다. outgoing 객체 전체를 deps 에 넣으면 rollDone 이 outgoing 을
    // null 로 바꾸는 것에 이 effect 가 다시 반응해 자기 취소 버그가 재발한다.
    // eslint-disable-next-line react-hooks/exhaustive-deps -- key 변화에만 반응(위 주석)
  }, [outgoing?.key]);

  // settle: 롤링 종료 후 새 내용의 실제 높이가 고정 높이와 다를 때만 트랜지션으로 옮긴 뒤
  // auto 로 되돌린다. 이미지 로드를 기다리지 않는다 — 이미지 영역은 로드 전에도 최소
  // 높이(2400:1260)를 차지하고, 로드 후 더 커지면 auto 복귀 뒤 자연히 늘어난다. (로드 대기
  // 방식은 느린 네트워크에서 새 카드가 이전 높이에 갇혀 버튼이 잘리는 결함을 만들어 제거했다 — 코드리뷰 HIGH.)
  useEffect(() => {
    if (settleSeq === 0) return;
    const current = currentRef.current;
    if (!current || !wrapRef.current) {
      dispatch({
        type: 'releaseHeight'
      });
      return;
    }
    const target = current.offsetHeight;
    const locked = wrapRef.current.offsetHeight;
    if (Math.abs(target - locked) <= 1) {
      dispatch({
        type: 'releaseHeight'
      });
      return;
    }
    dispatch({
      type: 'settleTo',
      heightPx: target
    });
    const releaseTimer = window.setTimeout(() => dispatch({
      type: 'releaseHeight'
    }), ROLL_MS);
    return () => window.clearTimeout(releaseTimer);
  }, [settleSeq]);
  useEffect(() => {
    ensureSwapKeyframes();
  }, []);
  return <div ref={wrapRef}
  // prefers-reduced-motion 사용자는 주입된 media rule 이 이 속성으로 모션을 끈다
  data-crm-swap style={{
    position: 'relative',
    overflow: 'hidden',
    height,
    // 높이 고정(전환 중)→새 값 이동은 트랜지션으로 조용히. auto 복귀는 즉시.
    transition: height ? `height ${ROLL_MS}ms ease-out` : undefined
  }} data-sentry-component="ContentSwap" data-sentry-source-file="NoticeCard.tsx">
      {outgoing && <div key={outgoing.key}
    // 퇴장 중 이전 캠페인이 스크린리더에 이중 노출되거나 키보드로 동작하지 않게 막는다.
    // aria-hidden 은 접근성 트리만, inert 는 포커스·키보드 상호작용까지 차단한다.
    aria-hidden
    // React 18 타입에는 inert가 없고 boolean 속성으로도 직렬화하지 않으므로 DOM 속성으로 설정한다.
    ref={element => element?.setAttribute('inert', '')} data-crm-swap style={{
      position: 'absolute',
      top: 0,
      left: 0,
      right: 0,
      // 퇴장 중인 이전 캠페인의 버튼/닫기가 클릭되지 않게 차단
      pointerEvents: 'none',
      animation: `crm-notice-roll-out ${ROLL_MS}ms cubic-bezier(0.4, 0, 0.2, 1) forwards`
    }}>
          {outgoing.node}
        </div>}
      <div key={displayedKey} ref={currentRef} data-crm-swap style={outgoing ? {
      animation: `crm-notice-roll-in ${ROLL_MS}ms cubic-bezier(0.4, 0, 0.2, 1) both`
    } : undefined}>
        {children}
      </div>
    </div>;
}
export function NoticeCard({
  campaign,
  actions,
  desktopOffset
}: NoticeCardProps) {
  // 재마운트가 없으므로 닫힘을 boolean 대신 "닫힌 캠페인 id" 로 관리한다. 다음 캠페인이
  // 오면 id 가 달라져 자동으로 다시 활성화된다(재마운트 초기화의 상태 모델 대체).
  const [dismissedId, setDismissedId] = useState<string | null>(null);
  const isActive = dismissedId !== campaign.id;
  const isMobile = useIsMobile();
  const title = campaign.title || '';
  const subText = campaign.body || '';
  const image = campaign.imageUrl || '';
  const renderType = (() => {
    if (isMobile) {
      return 'mobile';
    }
    return image ? 'withImage' : 'withoutImage';
  })();

  // 닫기(X): 서버 dismiss 트래킹 후 캠페인 제거. 공지의 로컬 24h 숨김 대신 CRM 은
  // 서버(maxImpressionPerUser/dismissForToday)가 재노출을 관장한다.
  const onClose = () => {
    setDismissedId(campaign.id);
    void actions.trackDismiss().catch(() => undefined);
    actions.close();
  };
  const onTrackClick = () => {
    return actions.trackClick();
  };
  if (!isActive) return null;
  if (renderType === 'mobile') {
    return <Container isMobile className={'bg-default'}>
        <CloseIconButton size={'sm'} iconSize={'md'} className={cn('p-0', 'absolute right-[-12px] top-[-16px] w-[36px] ', 'shadow-sm')} onClick={onClose} />
        <div className={'flex flex-col gap-md'}>
          <div className={'flex justify-between gap-md'}>
            <div className={'flex flex-col gap-2xs'}>
              <Title className={'gap-md'}>{title}</Title>
              <Content>{subText}</Content>
            </div>
            {image && <img className={'size-[58px] rounded-sm object-cover object-center'} alt={'notice-img'} src={image} />}
          </div>
          <Buttons campaign={campaign} onTrackClick={onTrackClick} />
        </div>
      </Container>;
  }

  // 데스크톱은 프레임(Container)을 유지하고 안의 내용만 ContentSwap 으로 교체한다.
  // withImage/withoutImage 는 프레임 패딩이 달라(p-0 vs p-md) 유형이 다른 캠페인끼리
  // 회전하면 패딩은 즉시 바뀐다 — 내용 롤링이 그 순간을 가리므로 허용하는 알려진 한계.
  const desktopBody = renderType === 'withImage' ? <>
        <div className={'self-stretch rounded-t-md'}>
          {/* 원본 비율대로 보여주되 2400:1260(가로형)~1:1(정사각형) 사이로 제한한다.
              범위를 넘는 이미지만 중앙 크롭한다. 최소 높이는 로드 전 자리를 잡아 캐러셀
              높이 출렁임을 줄이고, 최대 높이는 세로로 긴 이미지가 화면을 덮지 않게 한다.
              (338px = 카드 폭 340 - 테두리 2, 177px = 338 × 1260/2400) */}
          <img className={'h-auto max-h-[338px] min-h-[177px] w-full shrink-0 rounded-t-md object-cover object-center'} src={image} alt="notice-img" />
          <CloseIconButton className={'absolute right-[7px] top-[7px] size-[26px] p-3xs'} iconSize={'xs'} onClick={onClose} />
        </div>
        <div className={'flex flex-col gap-[14px] rounded-b-md bg-default p-md'}>
          <div className={'flex flex-col gap-2xs'}>
            <Title>{title}</Title>
            <Content className={'opacity-80'}>{subText}</Content>
          </div>
          <Buttons campaign={campaign} onTrackClick={onTrackClick} />
        </div>
      </> : <div className={'flex flex-col gap-[14px]'}>
        <div className={'flex flex-col gap-2xs'}>
          <Title rightSlot={<CloseIconButton className={'size-[26px] p-3xs'} onClick={onClose} />}>
            {title}
          </Title>
          <Content>{subText}</Content>
        </div>
        <Buttons campaign={campaign} onTrackClick={onTrackClick} />
      </div>;
  return <Container isMobile={false} style={{
    bottom: desktopOffset?.bottom,
    right: desktopOffset?.right
  }} className={renderType === 'withImage' ? 'p-0' : 'bg-default'} data-sentry-element="Container" data-sentry-component="NoticeCard" data-sentry-source-file="NoticeCard.tsx">
      <ContentSwap swapKey={campaign.id} data-sentry-element="ContentSwap" data-sentry-source-file="NoticeCard.tsx">{desktopBody}</ContentSwap>
    </Container>;
}
const Container = ({
  isMobile,
  className,
  style,
  ...rest
}: ComponentPropsWithoutRef<'div'> & {
  isMobile: boolean;
}) => {
  return <div
  // z-notice(quiz 전용 토큰) 대신 inline z-index — 채널톡 등 떠있는 위젯 위.
  style={{
    ...style,
    zIndex: NOTICE_POPUP_Z_INDEX
  }} className={cn([
  // 데스크톱 한정: 등장 애니메이션(fade-left)은 최초 마운트 1회만 재생된다.
  // 캐러셀 회전은 재마운트 없이 ContentSwap 이 내용만 롤링으로 교체하므로
  // 여기 애니메이션은 다시 발동하지 않는다(모바일 분기는 animation-name 이
  // 없어 no-op). delay 를 두지 않는 이유: 첫 등장이 빈 화면 뒤에 시작되면 안 됨.
  'animate-once animate-duration-300 animate-ease-out', 'fixed rounded-md border-opacity-assistive border flex flex-col', 'pointer-events-auto', isMobile ? 'bottom-[25px] left-1/2 -translate-x-1/2 w-[calc(100vw-32px)] py-md px-lg shadow-xl' : 'right-md bottom-lg w-[340px] p-md animate-fade-left shadow-md', className])} {...rest} data-sentry-component="Container" data-sentry-source-file="NoticeCard.tsx" />;
};
const Buttons = ({
  campaign,
  onTrackClick
}: {
  campaign: ResolvedCampaign;
  onTrackClick: () => string;
}) => {
  const buttons = (campaign.buttons ?? []).filter(button => !!button.label && !!button.url).map(button => ({
    text: button.label,
    link: button.url as string
  }))
  // 정렬 반대로 해야함(메인이 우측으로 가야해서 변경됨)
  .reverse();
  const handleClick = (e: React.MouseEvent<HTMLButtonElement>, index: number) => {
    e.preventDefault();
    const link = buttons[index].link;
    // 서버 캠페인 URL 을 검증 없이 열지 않는다 — CenterModalCard 와 동일하게 http(s)/상대
    // 경로만 허용하고 javascript:/data: 등 위험 스킴은 차단(어드민 변조 대비 P1 보안).
    const safeLink = link ? toSafeOpenUrl(link) : null;
    if (!safeLink) return;
    const trackingId = onTrackClick();
    window.open(appendTrackingId(safeLink, trackingId), '_blank', 'noopener,noreferrer');
  };
  if (buttons.length === 0) return null;
  return <div className={'flex gap-2xs'} data-sentry-component="Buttons" data-sentry-source-file="NoticeCard.tsx">
      {buttons.map((button, index, all) => <Button key={index} title={button.text} className={'w-full'} intent={all.length === 2 && index === 0 ? 'neutral' : 'alternative'} size={'md'} onClick={event => handleClick(event, index)}>
          {button.text}
        </Button>)}
    </div>;
};
const Title = (props: ComponentPropsWithoutRef<'h3'> & {
  rightSlot?: ReactNode;
}) => {
  const {
    rightSlot,
    children,
    className,
    ...rest
  } = props;
  return <div className={cn('flex justify-between  gap-3xs text-body-lg-bold text-default', className)} {...rest} data-sentry-component="Title" data-sentry-source-file="NoticeCard.tsx">
      <span>{children}</span>
      {rightSlot}
    </div>;
};
const Content = (props: ComponentPropsWithoutRef<'div'>) => {
  const {
    className,
    ...rest
  } = props;
  return <div className={cn('whitespace-pre-line break-words', 'text-body-sm-medium text-neutral', className)} {...rest} data-sentry-component="Content" data-sentry-source-file="NoticeCard.tsx" />;
};
const CloseIconButton = (props: Pick<ButtonProps, 'intent' | 'size' | 'className' | 'onClick'> & {
  iconSize?: IconSize;
}) => {
  const {
    iconSize,
    ..._props
  } = props;
  return <Button intent={props.intent || 'neutral'} rounded={'full'} size={'2xs'} variant={'contained'} leftSlot={<XIcon fill={ICON_COLOR.neutral} size={iconSize} />} {..._props} data-sentry-element="Button" data-sentry-component="CloseIconButton" data-sentry-source-file="NoticeCard.tsx" />;
};